// =============================================================================
// MOCKUP de la plataforma de Combustible — datos para demo offline
// -----------------------------------------------------------------------------
// Objetivo: alimentar TODAS las pantallas de la app sin que ningun fetch real
// se vaya a la red. El archivo expone:
//   - DEMO_*           datasets (login, empresa, catalogos, cargas, sensores)
//   - generarCargasDemo / generarSensoresCombustible  (regeneracion dinamica)
//   - activarSesionDemo / esSesionDemo                (helpers de localStorage)
//
// El interceptor de fetch vive en ./mockFetch.js y consume estos datasets.
//
// Notas de shape:
//   - CARGA_A_FULL y ELIMINADO se devuelven numericos (0/1) tal cual los manda
//     el backend. La pantalla de cargas hace el map a "Si"/"No".
//   - FECHAHORA_CARGA se devuelve en formato "YYYY-MM-DD HH:mm:ss" para que
//     `new Date(...)` funcione en chartCard y la grilla lo reformatee.
// =============================================================================

// -----------------------------------------------------------------------------
// 1) USUARIO DEMO + EMPRESA
// -----------------------------------------------------------------------------
export const DEMO_LOGIN_CREDENTIALS = {
  usuario: "demo",
  pass: "demo2026",
};

// JWT con shape valido y exp dentro de 10 anios. Es necesario porque
// utils/token.parseJwt hace token.split(".")[1].replace(...) — un token que
// no tenga 3 partes base64url rompe el guard de sesion del layout.
function construirJwtDemo() {
  // Codificamos en base64 estandar y solo intercambiamos los chars no-URL-safe
  // ("+" y "/"). Dejamos el padding "=" porque parseJwt no lo re-agrega y
  // atob es estricto con el padding en algunos browsers.
  const base64url = (obj) => {
    const raw =
      typeof btoa === "function"
        ? btoa(JSON.stringify(obj))
        : Buffer.from(JSON.stringify(obj)).toString("base64");
    return raw.replace(/\+/g, "-").replace(/\//g, "_");
  };
  const header = base64url({ alg: "none", typ: "JWT" });
  const expSec = Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 365 * 10; // +10 anios
  const payload = base64url({
    sub: "demo",
    name: "Demo Boltrack",
    iat: Math.floor(Date.now() / 1000),
    exp: expSec,
  });
  // La signature no se valida en frontend, basta con que exista.
  return `${header}.${payload}.demosig`;
}

export const DEMO_JWT = construirJwtDemo();

export const DEMO_USUARIO = {
  token: DEMO_JWT,
  idEmpresa: "9999",
  idUsuario: "1001",
  nombreUsuario: "Demo Boltrack",
  username: "demo",
  email: "demo@boltrack.net",
  rol: "ADMINISTRADOR",
  esSubCuenta: "0",
  permisos: "11111111111111111111",
};

export const DEMO_EMPRESA_COLORES = {
  idEmpresa: "9999",
  colorPrimario: "#1f4e8c",
  colorSecundario: "#f4a23a",
  colorFondo: "#f5f7fa",
  logoUrl: "/logo-boltrack.png",
  nombreEmpresa: "DEMO TRANSPORTES BOLIVIA S.A.",
  idioma: "es",
};

// -----------------------------------------------------------------------------
// 2) CATALOGOS BASE
// -----------------------------------------------------------------------------
export const DEMO_TIPOS_COMBUSTIBLE = [
  { id: 1, value: "GASOLINA", label: "GASOLINA" },
  { id: 2, value: "DIESEL", label: "DIESEL" },
  { id: 3, value: "GASOLINA SUPER ETANOL 92", label: "GASOLINA SUPER ETANOL 92" },
  { id: 4, value: "GASOLINA PREMIUM", label: "GASOLINA PREMIUM" },
  { id: 5, value: "GNV", label: "GNV" },
];

// Precio referencial Bs/litro (precios subvencionados en Bolivia).
export const DEMO_PRECIOS_COMBUSTIBLE = {
  1: 3.74, // gasolina
  2: 3.72, // diesel
  3: 4.79, // super 92
  4: 6.25, // premium
  5: 1.66, // GNV (Bs/m3)
};

// Shape igual al endpoint /obtenerListaSurtidores
export const DEMO_SURTIDORES = [
  { ID_SURTIDOR: 1,  NOMBRE: "YPFB EL ALTO CENTRO",         CIUDAD: "LA PAZ" },
  { ID_SURTIDOR: 2,  NOMBRE: "YPFB SAN MIGUEL",             CIUDAD: "LA PAZ" },
  { ID_SURTIDOR: 3,  NOMBRE: "YPFB MIRAFLORES",             CIUDAD: "LA PAZ" },
  { ID_SURTIDOR: 4,  NOMBRE: "YPFB ZONA SUR",               CIUDAD: "LA PAZ" },
  { ID_SURTIDOR: 5,  NOMBRE: "PETROBRAS EQUIPETROL",        CIUDAD: "SANTA CRUZ" },
  { ID_SURTIDOR: 6,  NOMBRE: "PETROBRAS DOBLE VIA LA GUARDIA", CIUDAD: "SANTA CRUZ" },
  { ID_SURTIDOR: 7,  NOMBRE: "YPFB AV. BANZER 4TO ANILLO",  CIUDAD: "SANTA CRUZ" },
  { ID_SURTIDOR: 8,  NOMBRE: "YPFB AV. ALEMANA",            CIUDAD: "SANTA CRUZ" },
  { ID_SURTIDOR: 9,  NOMBRE: "YPFB QUILLACOLLO",            CIUDAD: "COCHABAMBA" },
  { ID_SURTIDOR: 10, NOMBRE: "YPFB AV. AMERICA",            CIUDAD: "COCHABAMBA" },
  { ID_SURTIDOR: 11, NOMBRE: "YPFB SACABA",                 CIUDAD: "COCHABAMBA" },
  { ID_SURTIDOR: 12, NOMBRE: "SURTIDOR INTERNO PLANTA",     CIUDAD: "EL ALTO" },
];

// Shape igual a obtener-options-conductores
export const DEMO_CONDUCTORES = [
  { id: 5001, nombre: "JUAN CARLOS",   apellidoPaterno: "MAMANI",     apellidoMaterno: "QUISPE",   carnet: "4567890 LP" },
  { id: 5002, nombre: "LUIS FERNANDO", apellidoPaterno: "CHOQUE",     apellidoMaterno: "VARGAS",   carnet: "3245671 LP" },
  { id: 5003, nombre: "MARIA ELENA",   apellidoPaterno: "FLORES",     apellidoMaterno: "ROJAS",    carnet: "7821345 SC" },
  { id: 5004, nombre: "PEDRO ANTONIO", apellidoPaterno: "GUTIERREZ",  apellidoMaterno: "LOPEZ",    carnet: "5612893 CB" },
  { id: 5005, nombre: "CARLOS EDUARDO",apellidoPaterno: "ROCHA",      apellidoMaterno: "TICONA",   carnet: "9876123 LP" },
  { id: 5006, nombre: "ANA LUCIA",     apellidoPaterno: "CONDORI",    apellidoMaterno: "HUANCA",   carnet: "4523891 SC" },
  { id: 5007, nombre: "JORGE ARMANDO", apellidoPaterno: "VILLARROEL", apellidoMaterno: "CRUZ",     carnet: "3456712 CB" },
  { id: 5008, nombre: "DAVID ALBERTO", apellidoPaterno: "MAMANI",     apellidoMaterno: "ALVAREZ",  carnet: "1234567 LP" },
  { id: 5009, nombre: "RAUL ESTEBAN",  apellidoPaterno: "PAREDES",    apellidoMaterno: "SANCHEZ",  carnet: "7890123 SC" },
  { id: 5010, nombre: "MIGUEL ANGEL",  apellidoPaterno: "TORRICO",    apellidoMaterno: "MENDOZA",  carnet: "6543210 CB" },
  { id: 5011, nombre: "ROBERTO CARLOS",apellidoPaterno: "FERNANDEZ",  apellidoMaterno: "GUZMAN",   carnet: "8765432 LP" },
  { id: 5012, nombre: "VICTOR HUGO",   apellidoPaterno: "MORALES",    apellidoMaterno: "QUIROGA",  carnet: "2109876 SC" },
];

// Flota de ~22 vehiculos, mezcla pensada para ejercitar todas las cards.
// Campos extra (idTipoVehiculo/marca/modelo/capTanque) cumplen el shape de
// READ-VEHICULOEMPRESA tambien.
export const DEMO_VEHICULOS = [
  // --- Camiones pesados (diesel, mucho gasto) ---
  { imei: "356938035643801", placa: "1234-ABC", marca: "MERCEDES",  modelo: "ACTROS 2546",     anno: 2020, tipoCombustibleId: 2, capacidadTanque: 400, ID_ACTIVO: "1234-ABC", tipoVehiculo: "CAMION PESADO",   rolDemo: "logistica" },
  { imei: "356938035643802", placa: "5678-DEF", marca: "VOLVO",     modelo: "FH 460",          anno: 2022, tipoCombustibleId: 2, capacidadTanque: 600, ID_ACTIVO: "5678-DEF", tipoVehiculo: "CAMION PESADO",   rolDemo: "logistica" },
  { imei: "356938035643803", placa: "9012-GHI", marca: "SCANIA",    modelo: "R 450",           anno: 2021, tipoCombustibleId: 2, capacidadTanque: 600, ID_ACTIVO: "9012-GHI", tipoVehiculo: "CAMION PESADO",   rolDemo: "logistica" },
  { imei: "356938035643804", placa: "3456-JKL", marca: "VOLVO",     modelo: "FMX 540",         anno: 2019, tipoCombustibleId: 2, capacidadTanque: 600, ID_ACTIVO: "3456-JKL", tipoVehiculo: "CAMION PESADO",   rolDemo: "anomalia" }, // consumo elevado
  { imei: "356938035643805", placa: "7890-MNO", marca: "MERCEDES",  modelo: "AXOR 3344",       anno: 2021, tipoCombustibleId: 2, capacidadTanque: 500, ID_ACTIVO: "7890-MNO", tipoVehiculo: "CAMION PESADO",   rolDemo: "logistica" },

  // --- Camionetas 4x4 diesel (operativos) ---
  { imei: "356938035643806", placa: "2345-PQR", marca: "TOYOTA",    modelo: "HILUX 4x4",       anno: 2022, tipoCombustibleId: 2, capacidadTanque: 80,  ID_ACTIVO: "2345-PQR", tipoVehiculo: "CAMIONETA 4X4",   rolDemo: "operativo" },
  { imei: "356938035643807", placa: "6789-STU", marca: "TOYOTA",    modelo: "HILUX 4x4",       anno: 2023, tipoCombustibleId: 2, capacidadTanque: 80,  ID_ACTIVO: "6789-STU", tipoVehiculo: "CAMIONETA 4X4",   rolDemo: "operativo" },
  { imei: "356938035643808", placa: "0123-VWX", marca: "NISSAN",    modelo: "FRONTIER NP300",  anno: 2021, tipoCombustibleId: 2, capacidadTanque: 75,  ID_ACTIVO: "0123-VWX", tipoVehiculo: "CAMIONETA 4X4",   rolDemo: "operativo" },
  { imei: "356938035643809", placa: "4567-YZA", marca: "FORD",      modelo: "RANGER XLT",      anno: 2022, tipoCombustibleId: 2, capacidadTanque: 80,  ID_ACTIVO: "4567-YZA", tipoVehiculo: "CAMIONETA 4X4",   rolDemo: "operativo" },
  { imei: "356938035643810", placa: "8901-BCD", marca: "MITSUBISHI",modelo: "L200 TRITON",     anno: 2020, tipoCombustibleId: 2, capacidadTanque: 75,  ID_ACTIVO: "8901-BCD", tipoVehiculo: "CAMIONETA 4X4",   rolDemo: "operativo" },

  // --- Vehiculos livianos gasolina (administracion / comerciales) ---
  { imei: "356938035643811", placa: "2468-EFG", marca: "TOYOTA",    modelo: "COROLLA",         anno: 2023, tipoCombustibleId: 1, capacidadTanque: 50,  ID_ACTIVO: "2468-EFG", tipoVehiculo: "AUTOMOVIL",       rolDemo: "administrativo" },
  { imei: "356938035643812", placa: "1357-HIJ", marca: "SUZUKI",    modelo: "SWIFT",           anno: 2022, tipoCombustibleId: 1, capacidadTanque: 42,  ID_ACTIVO: "1357-HIJ", tipoVehiculo: "AUTOMOVIL",       rolDemo: "administrativo" },
  { imei: "356938035643813", placa: "9876-KLM", marca: "KIA",       modelo: "RIO",             anno: 2023, tipoCombustibleId: 1, capacidadTanque: 45,  ID_ACTIVO: "9876-KLM", tipoVehiculo: "AUTOMOVIL",       rolDemo: "comercial" },
  { imei: "356938035643814", placa: "1928-NOP", marca: "HYUNDAI",   modelo: "ACCENT",          anno: 2022, tipoCombustibleId: 1, capacidadTanque: 43,  ID_ACTIVO: "1928-NOP", tipoVehiculo: "AUTOMOVIL",       rolDemo: "comercial" },

  // --- Premium (ejecutivos) ---
  { imei: "356938035643815", placa: "5040-QRS", marca: "TOYOTA",    modelo: "FORTUNER",        anno: 2023, tipoCombustibleId: 4, capacidadTanque: 80,  ID_ACTIVO: "5040-QRS", tipoVehiculo: "SUV",             rolDemo: "ejecutivo" },
  { imei: "356938035643816", placa: "5050-TUV", marca: "FORD",      modelo: "EXPLORER",        anno: 2022, tipoCombustibleId: 4, capacidadTanque: 90,  ID_ACTIVO: "5050-TUV", tipoVehiculo: "SUV",             rolDemo: "ejecutivo" },

  // --- GNV (utilitarios urbanos, alta eficiencia) ---
  { imei: "356938035643817", placa: "3030-WXY", marca: "CHEVROLET", modelo: "N300 (GNV)",      anno: 2021, tipoCombustibleId: 5, capacidadTanque: 45,  ID_ACTIVO: "3030-WXY", tipoVehiculo: "UTILITARIO",      rolDemo: "estrella" }, // muy eficiente
  { imei: "356938035643818", placa: "4040-ZAB", marca: "RENAULT",   modelo: "KANGOO (GNV)",    anno: 2022, tipoCombustibleId: 5, capacidadTanque: 50,  ID_ACTIVO: "4040-ZAB", tipoVehiculo: "UTILITARIO",      rolDemo: "estrella" },

  // --- Buses (diesel, alta capacidad) ---
  { imei: "356938035643819", placa: "6060-CDE", marca: "MERCEDES",  modelo: "OF 1721 (BUS)",   anno: 2019, tipoCombustibleId: 2, capacidadTanque: 300, ID_ACTIVO: "6060-CDE", tipoVehiculo: "BUS",             rolDemo: "logistica" },
  { imei: "356938035643820", placa: "7070-FGH", marca: "VOLVO",     modelo: "B7R (BUS)",       anno: 2020, tipoCombustibleId: 2, capacidadTanque: 350, ID_ACTIVO: "7070-FGH", tipoVehiculo: "BUS",             rolDemo: "logistica" },

  // --- Maquinaria (super alto consumo, baja recurrencia) ---
  { imei: "356938035643821", placa: "EXC-001",  marca: "CATERPILLAR",modelo: "320D EXCAVADORA",anno: 2018, tipoCombustibleId: 2, capacidadTanque: 410, ID_ACTIVO: "EXC-001",  tipoVehiculo: "MAQUINARIA",      rolDemo: "obra" },
  { imei: "356938035643822", placa: "CAR-001",  marca: "JCB",       modelo: "3CX RETROEXCAVADORA", anno: 2020, tipoCombustibleId: 2, capacidadTanque: 160, ID_ACTIVO: "CAR-001",  tipoVehiculo: "MAQUINARIA",      rolDemo: "obra" },
];

// Usuarios del sistema (login a la app movil de registro de cargas).
export const DEMO_USUARIOS_SISTEMA = [
  { ID_USUARIO: 1001, NOMBREUSUARIO: "DEMO BOLTRACK",         USERNAME: "demo",      PASSWORD: "demo2026", ID_CONDUCTOR: null, ELIMINADO: 0 },
  { ID_USUARIO: 1002, NOMBREUSUARIO: "JUAN CARLOS MAMANI",    USERNAME: "jmamani",   PASSWORD: "abc12345", ID_CONDUCTOR: 5001, ELIMINADO: 0 },
  { ID_USUARIO: 1003, NOMBREUSUARIO: "LUIS FERNANDO CHOQUE",  USERNAME: "lchoque",   PASSWORD: "abc12345", ID_CONDUCTOR: 5002, ELIMINADO: 0 },
  { ID_USUARIO: 1004, NOMBREUSUARIO: "MARIA ELENA FLORES",    USERNAME: "mflores",   PASSWORD: "abc12345", ID_CONDUCTOR: 5003, ELIMINADO: 0 },
  { ID_USUARIO: 1005, NOMBREUSUARIO: "PEDRO GUTIERREZ",       USERNAME: "pgutierrez",PASSWORD: "abc12345", ID_CONDUCTOR: 5004, ELIMINADO: 0 },
  { ID_USUARIO: 1006, NOMBREUSUARIO: "CARLOS ROCHA (BAJA)",   USERNAME: "crocha",    PASSWORD: "abc12345", ID_CONDUCTOR: 5005, ELIMINADO: 1 },
  { ID_USUARIO: 1007, NOMBREUSUARIO: "ANA LUCIA CONDORI",     USERNAME: "acondori",  PASSWORD: "abc12345", ID_CONDUCTOR: 5006, ELIMINADO: 0 },
  { ID_USUARIO: 1008, NOMBREUSUARIO: "JORGE VILLARROEL",      USERNAME: "jvillarroel",PASSWORD:"abc12345", ID_CONDUCTOR: 5007, ELIMINADO: 0 },
  { ID_USUARIO: 1009, NOMBREUSUARIO: "DAVID MAMANI",          USERNAME: "dmamani",   PASSWORD: "abc12345", ID_CONDUCTOR: 5008, ELIMINADO: 0 },
  { ID_USUARIO: 1010, NOMBREUSUARIO: "RAUL PAREDES",          USERNAME: "rparedes",  PASSWORD: "abc12345", ID_CONDUCTOR: 5009, ELIMINADO: 0 },
  { ID_USUARIO: 1011, NOMBREUSUARIO: "MIGUEL TORRICO",        USERNAME: "mtorrico",  PASSWORD: "abc12345", ID_CONDUCTOR: 5010, ELIMINADO: 0 },
];

// -----------------------------------------------------------------------------
// 3) CARGAS DE COMBUSTIBLE
// -----------------------------------------------------------------------------
// Misma forma que devuelve /combustibleapi/getListaCargas crudo (sin el map
// que hace CombustibleCargas). CARGA_A_FULL y ELIMINADO son numericos.

function pad2(n) { return String(n).padStart(2, "0"); }
function fmtFechaSql(d) {
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
    `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
  );
}

// PRNG deterministico (mulberry32). Semilla fija → demo reproducible.
function seededRandom(seed) {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6d2b79f5) >>> 0;
    let x = t;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

// Multiplicadores de consumo por rol — afectan litros/km efectivo para que la
// historia de la demo tenga vehiculos eficientes vs gastadores.
const CONSUMO_POR_ROL = {
  logistica:     { litrosPorKm: 0.32, fillProb: 0.55 },
  operativo:     { litrosPorKm: 0.11, fillProb: 0.40 },
  administrativo:{ litrosPorKm: 0.07, fillProb: 0.30 },
  comercial:     { litrosPorKm: 0.08, fillProb: 0.30 },
  ejecutivo:     { litrosPorKm: 0.13, fillProb: 0.45 },
  estrella:      { litrosPorKm: 0.05, fillProb: 0.50 }, // GNV eficiente
  anomalia:      { litrosPorKm: 0.55, fillProb: 0.20 }, // consumo elevado, pocos llenos
  obra:          { litrosPorKm: 1.50, fillProb: 0.60 }, // maquinaria, "km" son horas trabajadas
};

export function generarCargasDemo({ dias = 60, semilla = 20260601 } = {}) {
  const rnd = seededRandom(semilla);
  const pick = (arr) => arr[Math.floor(rnd() * arr.length)];

  // Conductor habitual por vehiculo (estable entre cargas).
  const conductorPorVehiculo = new Map(
    DEMO_VEHICULOS.map((v, i) => [v.placa, DEMO_CONDUCTORES[i % DEMO_CONDUCTORES.length]]),
  );

  // Odometro inicial por vehiculo.
  const odometroPorVehiculo = new Map(
    DEMO_VEHICULOS.map((v) => [v.placa, 25000 + Math.floor(rnd() * 90000)]),
  );

  const tipoComboPorId = new Map(DEMO_TIPOS_COMBUSTIBLE.map((t) => [t.id, t.value]));

  const cargas = [];
  let idCounter = 700000;
  const ahora = new Date();

  // Para cada dia, decidimos para cada vehiculo si carga, segun su perfil.
  for (let d = dias; d >= 0; d--) {
    for (const vehiculo of DEMO_VEHICULOS) {
      const perfil = CONSUMO_POR_ROL[vehiculo.rolDemo] || CONSUMO_POR_ROL.operativo;
      // Probabilidad de cargar este dia, depende del rol.
      const cargaHoy = (() => {
        if (vehiculo.rolDemo === "obra") return rnd() < 0.35;
        if (vehiculo.rolDemo === "logistica") return rnd() < 0.55;
        if (vehiculo.rolDemo === "administrativo") return rnd() < 0.18;
        return rnd() < 0.30;
      })();
      if (!cargaHoy) continue;

      const conductor = conductorPorVehiculo.get(vehiculo.placa);
      const surtidor = pick(DEMO_SURTIDORES);
      const tipoCombustibleId = vehiculo.tipoCombustibleId;
      const tipoCombustible = tipoComboPorId.get(tipoCombustibleId);

      // Cantidad cargada — entre 50% y 95% del tanque.
      const fraccionTanque = 0.50 + rnd() * 0.45;
      const cantidad = Math.round(vehiculo.capacidadTanque * fraccionTanque * 10) / 10;

      const precio = DEMO_PRECIOS_COMBUSTIBLE[tipoCombustibleId] || 3.74;
      const consumo = Math.round(cantidad * precio * 100) / 100;

      // Km consumidos = cantidad / litrosPorKm (con dispersion).
      const kmAprox = cantidad / perfil.litrosPorKm * (0.85 + rnd() * 0.3);
      const km = Math.max(50, Math.round(kmAprox));
      const medidorActual = (odometroPorVehiculo.get(vehiculo.placa) || 0) + km;
      odometroPorVehiculo.set(vehiculo.placa, medidorActual);

      const fechaCarga = new Date(ahora);
      fechaCarga.setDate(fechaCarga.getDate() - d);
      fechaCarga.setHours(5 + Math.floor(rnd() * 16), Math.floor(rnd() * 60), Math.floor(rnd() * 60), 0);

      const cargaFull = rnd() < perfil.fillProb ? 1 : 0;
      const nombreConductor = `${conductor.apellidoPaterno} ${conductor.apellidoMaterno} ${conductor.nombre}`.trim();
      const usuarioRegistra =
        DEMO_USUARIOS_SISTEMA.find((u) => u.ID_CONDUCTOR === conductor.id)?.NOMBREUSUARIO ||
        "DEMO BOLTRACK";

      cargas.push({
        ID_CARGA: String(++idCounter),
        FECHAHORA_CARGA: fmtFechaSql(fechaCarga),
        ACTIVO: vehiculo.placa,
        DESCRIPCION: `${vehiculo.marca} ${vehiculo.modelo}`,
        USUARIO: usuarioRegistra,
        CONDUCTOR: nombreConductor,
        ID_CONDUCTOR: conductor.id,
        SURTIDOR: surtidor.NOMBRE,
        ID_SURTIDOR: surtidor.ID_SURTIDOR,
        tipoCombustible,
        ID_TIPO_COMBUSTIBLE: tipoCombustibleId,
        CANTIDAD: cantidad,
        CONSUMO: consumo,
        MEDIDOR: medidorActual,
        CARGA_A_FULL: cargaFull,           // numerico crudo: 0/1
        NRO_FACTURA: `F-${100000 + Math.floor(rnd() * 899999)}`,
        OBSERVACIONES: cargaFull === 1 ? "Carga a tanque lleno" : "",
        CODIGO_QR: "",
        URL_FOTO_MEDIDOR: "uploads/demo-placeholder.jpg",
        ELIMINADO: rnd() < 0.025 ? 1 : 0,  // ~2.5% anuladas
      });
    }
  }

  // Ordenadas descendente por fecha (mas reciente primero).
  return cargas.sort((a, b) => (a.FECHAHORA_CARGA < b.FECHAHORA_CARGA ? 1 : -1));
}

export const DEMO_CARGAS = generarCargasDemo();

// -----------------------------------------------------------------------------
// 4) SENSORES DE COMBUSTIBLE (tiempo real, monitoreo + dashboard list card)
// -----------------------------------------------------------------------------
// El endpoint RP003BodyV11.rep devuelve { data: "<header>?<row1>?<row2>?..." }
// con campos separados por "|". Generamos un string asi para cada vehiculo.

const SENSOR_HEADERS = [
  "TIPO_SENSOR",
  "NOMBRE_SENSOR",
  "ID_ACTIVO",
  "FECHA_EVENTO",
  "VALOR_SENSOR",
  "UNIDAD_DE_MEDIDA",
];

function fechaSensor(d) {
  return (
    `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ` +
    `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
  );
}

// Nivel actual por placa, persistido entre llamadas para que el dashboard no
// vea saltos bruscos al refrescar cada 15s. El mapa vive en memoria del modulo;
// se reinicia cuando la SPA recarga, lo cual es aceptable para una demo.
const nivelCombustiblePorPlaca = new Map();

// Genera un snapshot de nivel de combustible "ahora" para una placa.
// El nivel evoluciona de forma continua: baja un poco en cada llamada y, al
// llegar a reserva, simula una recarga a tanque casi lleno.
export function generarSensoresCombustible(placa, semilla) {
  // Seed estable por placa — sin Date.now() — para que el ruido aleatorio
  // (cantidad de lecturas, jitter) tampoco salte cada refresh.
  const seedBase = placa.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const rnd = seededRandom(semilla ?? seedBase);
  const ahora = new Date();
  const lineas = [SENSOR_HEADERS.join("|")];

  // Estado persistente del tanque.
  let nivelActual = nivelCombustiblePorPlaca.get(placa);
  if (nivelActual == null) {
    // Primer acceso: distribuir niveles iniciales para que la lista del
    // dashboard arranque con colores variados (rojo/amarillo/verde).
    nivelActual = 25 + rnd() * 65; // entre 25% y 90%
  } else {
    // Consumo entre refresh y refresh (~0.1-0.6% por llamada).
    nivelActual = nivelActual - (0.1 + rnd() * 0.5);
    // Recarga simulada cuando entra en reserva.
    if (nivelActual < 10) {
      nivelActual = 85 + rnd() * 10; // tanque casi lleno
    }
  }

  // 4-6 lecturas historicas en la ultima hora; las mas antiguas tienen un
  // poquito mas de nivel para reflejar que el tanque venia bajando.
  const lecturas = 4 + Math.floor(rnd() * 3);
  for (let i = lecturas; i >= 0; i--) {
    const fecha = new Date(ahora.getTime() - i * 9 * 60 * 1000); // cada ~9 min
    const nivelLectura = Math.min(100, Math.max(0, nivelActual + i * (0.1 + rnd() * 0.2)));
    lineas.push(
      [
        "Combustible",
        "Tanque principal",
        placa,
        fechaSensor(fecha),
        nivelLectura.toFixed(1).replace(".", ","),
        "%",
      ].join("|"),
    );
  }

  nivelCombustiblePorPlaca.set(placa, nivelActual);
  return lineas.join("?") + "?"; // trailing "?" como el reporte original
}

// -----------------------------------------------------------------------------
// 4.b) REPORTE "CONSUMO POR VEHICULO" (RP007BodyV02.rep)
// -----------------------------------------------------------------------------
// El endpoint real devuelve { data: "<header>?<row1>?<row2>?..." } con las
// columnas: MES | TIPO COMBUSTIBLE | GRUPO | SUBGRUPO | NOMBRE VEHICULO |
// NRO DE CARGAS | CANTIDAD DE COMBUSTIBLE | TOTAL COSTO.
// Agregamos DEMO_CARGAS por mes/tipo/vehiculo para producir el mismo string.

// El tipo combustible en el reporte se muestra "bonito" (Diesel, Gasolina,
// Gasolina Super Etanol 92, GNV, S/N) — mapeado por id para no depender del
// casing del label.
const TIPO_COMBUSTIBLE_LABEL_REPORTE = {
  1: "Gasolina",
  2: "Diesel",
  3: "Gasolina Super Etanol 92",
  4: "Gasolina Premium",
  5: "GNV",
};

export function generarConsumoPorVehiculo({
  desdeUnix,
  hastaUnix,
  placa,                 // "" | "TODOS" = todos
  tipoCombustibleLabel,  // "" | "TODOS" = todos (comparado case-insensitive)
  idSurtidor,            // "" = todos
} = {}) {
  const HEADER =
    "MES|TIPO COMBUSTIBLE|GRUPO|SUBGRUPO|NOMBRE VEHICULO|" +
    "NRO DE CARGAS|CANTIDAD DE COMBUSTIBLE|TOTAL COSTO";

  const ini = Number(desdeUnix || 0);
  const fin = Number(hastaUnix || Math.floor(Date.now() / 1000));
  const placaFiltro = placa && placa !== "TODOS" ? String(placa) : "";
  const tipoFiltro  = String(tipoCombustibleLabel || "").trim().toUpperCase();
  const surtFiltro  = idSurtidor ? String(idSurtidor) : "";

  const vehPorPlaca = new Map(DEMO_VEHICULOS.map((v) => [v.placa, v]));
  const grupos = new Map();

  for (const c of DEMO_CARGAS) {
    if (Number(c.ELIMINADO) === 1) continue;

    const ts = Math.floor(new Date(c.FECHAHORA_CARGA).getTime() / 1000);
    if (ts < ini || ts > fin) continue;
    if (placaFiltro && c.ACTIVO !== placaFiltro) continue;
    if (
      tipoFiltro &&
      tipoFiltro !== "TODOS" &&
      String(c.tipoCombustible || "").toUpperCase() !== tipoFiltro
    ) continue;
    if (surtFiltro && String(c.ID_SURTIDOR) !== surtFiltro) continue;

    const veh = vehPorPlaca.get(c.ACTIVO);
    const fecha = new Date(c.FECHAHORA_CARGA);
    const mes = `${fecha.getFullYear()}-${pad2(fecha.getMonth() + 1)}`;
    const tipoLabel =
      TIPO_COMBUSTIBLE_LABEL_REPORTE[Number(c.ID_TIPO_COMBUSTIBLE)] || "S/N";
    const grupo = DEMO_EMPRESA_COLORES.nombreEmpresa;
    const subgrupo = veh?.tipoVehiculo || "SIN CATEGORIA";
    const nombreVehiculo = veh
      ? `${veh.placa} ${veh.marca} ${veh.modelo}`
      : c.ACTIVO;

    const key = [mes, tipoLabel, grupo, subgrupo, nombreVehiculo].join("|");
    const acc = grupos.get(key) || {
      mes, tipoLabel, grupo, subgrupo, nombreVehiculo,
      cargas: 0, cantidad: 0, costo: 0,
    };
    acc.cargas   += 1;
    acc.cantidad += Number(c.CANTIDAD) || 0;
    acc.costo    += Number(c.CONSUMO)  || 0;
    grupos.set(key, acc);
  }

  const filas = [...grupos.values()]
    .sort((a, b) => {
      if (a.mes !== b.mes) return a.mes < b.mes ? 1 : -1; // mes desc
      if (a.tipoLabel !== b.tipoLabel) return a.tipoLabel < b.tipoLabel ? -1 : 1;
      return a.nombreVehiculo < b.nombreVehiculo ? -1 : 1;
    })
    .map((g) => [
      g.mes,
      g.tipoLabel,
      g.grupo,
      g.subgrupo,
      g.nombreVehiculo,
      g.cargas,
      g.cantidad.toFixed(2),
      g.costo.toFixed(2),
    ].join("|"));

  return [HEADER, ...filas].join("?") + "?"; // trailing "?" como el reporte original
}

// -----------------------------------------------------------------------------
// 5) DASHBOARD — layout pre-armado
// -----------------------------------------------------------------------------
export const DEMO_DASHBOARD_LAYOUT = {
  layouts: {
    lg: [
      { i: "1", x: 0, y: 0,  w: 6, h: 8 },
      { i: "2", x: 6, y: 0,  w: 6, h: 8 },
      { i: "3", x: 0, y: 8,  w: 4, h: 8 },
      { i: "4", x: 4, y: 8,  w: 4, h: 8 },
      { i: "5", x: 8, y: 8,  w: 4, h: 8 },
      { i: "6", x: 0, y: 16, w: 6, h: 8 },
      { i: "7", x: 6, y: 16, w: 6, h: 8 },
    ],
    md: [], sm: [], xs: [], xxs: [],
  },
  items: [
    { i: "1", type: "comb-gasto-mensual",    title: "Gasto mensual",            rangePreset: "ultimos-6-meses", monthsBack: 6, selectedImeis: [] },
    { i: "2", type: "comb-top-vehiculos",    title: "Top vehículos por gasto",  rangePreset: "mes", sortOrder: "desc", limitBars: 10, barOrientation: "horizontal", selectedImeis: [] },
    { i: "3", type: "comb-top-usuarios",     title: "Top usuarios",             rangePreset: "mes", sortOrder: "desc", limitBars: 5,  barOrientation: "horizontal", selectedImeis: [] },
    { i: "4", type: "comb-top-surtidores",   title: "Top surtidores",           rangePreset: "mes", sortOrder: "desc", limitBars: 5,  barOrientation: "horizontal", selectedImeis: [] },
    { i: "5", type: "comb-tipo-combustible", title: "Por tipo de combustible",  rangePreset: "mes", barOrientation: "vertical", selectedImeis: [] },
    { i: "6", type: "comb-rendimiento",      title: "Rendimiento km/litro",     rangePreset: "mes", sortOrder: "desc", limitBars: 10, barOrientation: "horizontal", selectedImeis: [] },
    { i: "7", type: "comb-lista-sensor",     title: "Lista combustible (live)", horas: 2, selectedImeisOptions: [] },
  ],
};

// -----------------------------------------------------------------------------
// 6) HELPERS DE SESION
// -----------------------------------------------------------------------------
export const DEMO_FLAG_KEY = "demo_mode_active";

export function activarSesionDemo() {
  localStorage.setItem("accessToken", DEMO_USUARIO.token);
  localStorage.setItem("idEmpresa", DEMO_USUARIO.idEmpresa);
  localStorage.setItem("usuario", JSON.stringify(DEMO_USUARIO));
  localStorage.setItem("colores", JSON.stringify(DEMO_EMPRESA_COLORES));
  localStorage.setItem(DEMO_FLAG_KEY, "1");
}

export function desactivarSesionDemo() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("idEmpresa");
  localStorage.removeItem("usuario");
  localStorage.removeItem("colores");
  localStorage.removeItem(DEMO_FLAG_KEY);
}

export function esSesionDemo() {
  try {
    if (localStorage.getItem(DEMO_FLAG_KEY) === "1") return true;
    const raw = localStorage.getItem("usuario");
    if (!raw) return false;
    return JSON.parse(raw).idEmpresa === DEMO_USUARIO.idEmpresa;
  } catch {
    return false;
  }
}

// -----------------------------------------------------------------------------
// 7) Bundle de export para acceso rapido
// -----------------------------------------------------------------------------
export const DEMO = {
  credenciales: DEMO_LOGIN_CREDENTIALS,
  usuario: DEMO_USUARIO,
  empresaColores: DEMO_EMPRESA_COLORES,
  tiposCombustible: DEMO_TIPOS_COMBUSTIBLE,
  preciosCombustible: DEMO_PRECIOS_COMBUSTIBLE,
  surtidores: DEMO_SURTIDORES,
  conductores: DEMO_CONDUCTORES,
  vehiculos: DEMO_VEHICULOS,
  usuariosSistema: DEMO_USUARIOS_SISTEMA,
  cargas: DEMO_CARGAS,
  dashboard: DEMO_DASHBOARD_LAYOUT,
  generarConsumoPorVehiculo,
};

export default DEMO;
